import { SEOHeader } from '@/components/Headers';
import ListOfBookCopies from '@/components/Pages/Book/ListOfBookCopies';
import HelpIconButton from '@/components/common/Buttons/HelpIconButton';
import { Image } from "@heroui/react";
import Link from 'next/link';
import BookISBNDetails from '../../components/BookISBNDetails';
import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import axios from 'axios';
import { Tooltip } from "@heroui/react";
import { CalendarIcon } from '@heroicons/react/outline';
import FallbackLoader from '@/components/FallbackLoader';
import { createBookSeo } from '@/lib/formatters';
import UserArticles from '@/components/UserArticles';

export default function Book({ bookisbn, initialArticles = [], structuredData = null }) {
  const router = useRouter();
  const [bookCopies, setBookCopies] = useState([]);
  const [_isLoading, setLoading] = useState(true);

  // Fetch the book copies after the page has loaded
  useEffect(() => {
    const fetchBookCopies = async () => {
      try {
        const response = await axios.get(`${process.env.NEXT_PUBLIC_DJANGO_URL}/api/bookle/bookcopies/isbn?id=${bookisbn.id}`);
        setBookCopies(response.data.results);
        // BookCopies already include count in the response
      } catch (error) {
        console.error('Error fetching book copies:', error);
      } finally {
        setLoading(false);
      }
    };
    if (bookisbn) {
      fetchBookCopies();
    }
  }, [bookisbn?.slug]);  // Trigger this effect when the book slug changes

  // Enhanced SEO metadata with null checks
  const seo = {
    ...createBookSeo(bookisbn),
    openGraph: {
      title: bookisbn?.title || 'Książka na Lektuarium',
      description: bookisbn?.description || 'Szczegóły książki, artykuły, egzemplarze i opinie',
      type: 'book',
      images: [
        {
          url: bookisbn?.thumbnailBig || bookisbn?.thumbnailMedium || '/images/book_cover_unavailable.webp',
          width: 800,
          height: 600,
          alt: `Okładka książki ${bookisbn?.title || ''}`,
        }
      ],
      book: {
        authors: bookisbn?.author ? [bookisbn.author] : [],
        ...(bookisbn?.isbn && { isbn: bookisbn.isbn }),
        ...(bookisbn?.publication_date && { releaseDate: bookisbn.publication_date }),
      }
    },
    additionalMetaTags: [
      {
        name: 'keywords',
        content: `${bookisbn?.title || ''}, ${bookisbn?.author || ''}, książka, lektura, recenzje, egzemplarze, artykuły`
      },
      {
        name: 'twitter:label1',
        content: 'Autor'
      },
      {
        name: 'twitter:data1',
        content: bookisbn?.author || 'Nieznany'
      }
    ]
  };

  return (
    <>
      {router.isFallback ?
        <FallbackLoader router={router}></FallbackLoader>
        :
        (
          <>
            <SEOHeader {...seo} />
            {structuredData && (
              <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{
                  __html: JSON.stringify(structuredData)
                }}
              />
            )}
            <div className="bg-white">
              <div className="mx-auto py-5 px-4 sm:py-24 sm:px-6 lg:max-w-7xl lg:py-16 lg:px-8">
                {/* Book */}
                <div className="lg:grid lg:grid-cols-7 lg:grid-rows-1 lg:gap-x-8 lg:gap-y-10 xl:gap-x-16">
                  {/* Book details */}
                  <div className="mx-auto mt-14 max-w-2xl sm:mt-16 lg:col-span-3 lg:row-span-2 lg:row-end-2 lg:mt-0 lg:max-w-none">
                    <div className="flex flex-col-reverse">
                      <div className="mt-4">
                        <h1 className="font-baskervil text-4xl font-bold tracking-tight text-gray-900 sm:text-3xl">
                          {bookisbn.title}
                        </h1>
                        {bookisbn.subtitle && (
                          <h2 className="text-xl font-medium tracking-tight text-gray-600 sm:text-xl">
                            {bookisbn.subtitle}
                          </h2>
                        )}
                        <div className='flex flex-row justify-between'>
                          <h3 id="information-heading" className="sr-only">
                            Informacje, położenie, recenzje i egzemplarze książki
                          </h3>
                          <h3 className="leading-2 mt-2 text-2xl font-cormorant">{bookisbn.author}</h3>
                          <p className="mt-2 text-sm text-gray-500 mr-10">
                            <Tooltip content={`Dodano ${new Date(bookisbn.created_at).toLocaleDateString()}`}>
                              <CalendarIcon className='h-5 w-5 text-gray-400' />
                            </Tooltip>
                          </p>
                        </div>
                      </div>

                      <div>
                        <h3 className="sr-only">Okładka książki</h3>
                        <Image
                          src={
                            bookisbn.thumbnailMedium
                              ? bookisbn.thumbnailMedium
                              : require('@/assets/images/book_cover_unavailable.webp').default.src
                          }
                          alt={`Okładka książki ${bookisbn.title}`}
                          width={300}
                          height={400}
                          isBlurred
                          isZoomed
                        />
                        <div className="flex items-center"></div>
                      </div>
                    </div>

                    <BookISBNDetails bookisbn={bookisbn}></BookISBNDetails>

                    <div className="mt-10 border-t border-gray-200 pt-10">
                      <h3 className="text-sm font-medium text-gray-900">Dołącz do społeczności</h3>
                      <p className="mt-4 text-sm text-gray-500">
                        Każdy egzemplarz posiada kartę biblioteczną. Załóż konto aby
                        skatalogować własne książki i wypożyczaj je lokalnie.
                      </p>
                    </div>
                    <div className="mt-10 border-t border-gray-200 pt-10">
                      <h3 className="flex text-sm font-medium text-gray-900">
                        Więcej <HelpIconButton />
                      </h3>
                      <div className=" my-auto text-sm text-gray-500">
                        Jeśli chcesz dowiedzieć się więcej, <Link className='font-sans font-semibold my-auto' href={'/blog'}>zajrzyj na bloga</Link>.
                      </div>
                    </div>
                  </div>

                  <div className="mx-auto mt-16 w-full max-w-2xl lg:col-span-4 lg:mt-0 lg:max-w-none">
                    <h2 className="text-2xl font-bold mb-6">Artykuły o tej książce</h2>
                    <UserArticles
                      bookisbnId={bookisbn.id}
                      size="small"
                      cols={2}
                      limit={4}
                      key={`articles-${bookisbn.id}-${Date.now()}`}
                      initialData={initialArticles}
                      isBookTitle={false}
                    />

                    <h2 className="text-2xl font-bold mt-12 mb-6">Egzemplarze książki</h2>
                    <ListOfBookCopies
                      bookCopies={bookCopies}
                      count={bookCopies ? bookCopies.length : 0}
                      isbnId={bookisbn.id}
                      customColumns={["thumbnail", "name", "city", "status", "actions"]}
                    />
                  </div>
                </div>
              </div>
            </div>
          </>)
      }

    </>
  );
}

Book.layout = 'Main';

export async function getStaticPaths() {

  let allBooks = [];
  let page = 1;
  let hasMorePages = true;

  while (hasMorePages) {
    const res = await fetch(`${process.env.NEXT_PUBLIC_BACKEND_URL}/api/bookle/isbn/?page=${page}`);
    const data = await res.json();

    allBooks = allBooks.concat(data.results);
    hasMorePages = data.next;  // Check if there are more pages to fetch
    page++;
  }

  // Map the slugs to paths
  const paths = allBooks.map((book) => ({
    params: { slug: book.slug },
  }));

  return {
    paths,
    fallback: true,
    // false: No new paths will be created, and missing pages will result in a 404.
    // true: New pages are created at runtime when requested (this can be useful, but pages may not be pre-built).
    // blocking: Ensures new pages are generated at build time if they aren’t pre-rendered yet.
  };
}

export async function getStaticProps({ params }) {
  let uri = `${process.env.NEXT_PUBLIC_BACKEND_URL}/api/bookle/isbn/${params.slug}`;

  // If url param is a number, change backend route
  if (!isNaN(params.slug)) {
    uri += '/';
  }

  try {
    // Fetch book data
    let bookRes = await fetch(uri);
    let bookisbn = await bookRes.json();

    if (!bookisbn) {
      return {
        notFound: true,  // Returns a 404 if the book is not found
      };
    }

    // Fetch initial articles related to this book
    const articlesRes = await fetch(
      `${process.env.NEXT_PUBLIC_BACKEND_URL}/api/tracks?bookisbn=${bookisbn.id}&limit=8`
    );
    const articlesData = await articlesRes.json();
    const initialArticles = articlesData.results || [];

    // Add structured data for SEO with null checks to prevent serialization errors
    const structuredData = {
      '@context': 'https://schema.org',
      '@type': 'Book',
      name: bookisbn.title || '',
      author: {
        '@type': 'Person',
        name: bookisbn.author || ''
      },
      // Only include properties if they exist
      ...(bookisbn.isbn && { isbn: bookisbn.isbn }),
      ...(bookisbn.description && { description: bookisbn.description }),
      ...(bookisbn.publisher && { publisher: bookisbn.publisher }),
      ...(bookisbn.publication_date && { datePublished: bookisbn.publication_date }),
      ...(bookisbn.thumbnailBig || bookisbn.thumbnailMedium
        ? { image: bookisbn.thumbnailBig || bookisbn.thumbnailMedium }
        : {})
    };

    return {
      props: {
        bookisbn,
        initialArticles,
        structuredData
      },
      revalidate: 15, // More frequent revalidation (15 seconds) to keep content fresh
    };
  } catch (error) {
    console.error('Error in getStaticProps:', error);
    return {
      notFound: true
    };
  }
}